import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { Element } from '@phrasee/phrasee-typings'
import { useQueryClient } from '@tanstack/react-query'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { useAppSelector } from 'common/hooks/redux'
import useGetMergeTagsQuery from 'features/admin/accountSettings/api/queries/useGetMergeTagsQuery'
import useGenerateBriefFromContentMutation from 'features/unifiedFlow/api/mutations/useGenerateBriefFromContentMutation'
import useGenerateCustomBriefFromContentMutation from 'features/unifiedFlow/api/mutations/useGenerateCustomBriefFromContentMutation'
import { ContentWithProgress } from 'features/unifiedFlow/api/queries/useGetContentQuery'
import { contentKeys } from 'features/unifiedFlow/api/queryKeys'

import useWebsocketMessage from '../../../hooks/websocketMessage'
import { getWsTopic } from '../../../hooks/websocketMessage/helpers'

export type GenerateBriefFromContentOptions = {
  brandVoiceId?: string
  mergeTagIds?: string[]
}

export const useBriefFromContentGeneration = ({
  content,
  selectedElement,
  isGlobalSettings,
  isOptimized,
  hasVariants,
  isModalOpenRef,
  setResetWarningModalState,
}: {
  content: ContentWithProgress | undefined
  selectedElement: Element | undefined
  isGlobalSettings: boolean
  isOptimized: boolean
  hasVariants: boolean | undefined
  isModalOpenRef: React.MutableRefObject<boolean>
  setResetWarningModalState: Dispatch<
    SetStateAction<{
      isOpen: boolean
      onConfirm?: () => void
    }>
  >
}) => {
  const flags = useFlags()
  const queryClient = useQueryClient()
  const { sendWebsocketMessage } = useWebsocketMessage()
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const { data: mergeTags } = useGetMergeTagsQuery()

  const generateBriefFromContentMutation = useGenerateBriefFromContentMutation()
  const generateCustomBriefFromContentMutation =
    useGenerateCustomBriefFromContentMutation()

  // Tracks the full async duration (POST + websocket round-trip) scoped
  // specifically to this generation, rather than any content-wide websocket
  // activity — otherwise an unrelated in-flight action (e.g. variants
  // generation) makes this show as "generating" too.
  const currentGeneratedBrief =
    !isGlobalSettings && selectedElement
      ? selectedElement.user_input?.generated_brief
      : content?.user_input.generated_brief
  const [isAwaitingResult, setIsAwaitingResult] = useState(false)
  const previousGeneratedBriefRef = useRef(currentGeneratedBrief)

  useEffect(() => {
    if (
      isAwaitingResult &&
      currentGeneratedBrief &&
      currentGeneratedBrief !== previousGeneratedBriefRef.current
    ) {
      setIsAwaitingResult(false)
    }
    previousGeneratedBriefRef.current = currentGeneratedBrief
  }, [currentGeneratedBrief, isAwaitingResult])

  // The content-api brief-from-content endpoints don't persist a "remove"
  // action yet, so this just clears the local marker — a fresh import simply
  // overwrites `raw_content`/`raw_content_type` server-side.
  const clearRawContent = () => {
    if (!content) {
      return
    }

    const elementId =
      !isGlobalSettings && selectedElement?.element_id
        ? selectedElement.element_id
        : undefined

    queryClient.setQueryData(
      contentKeys.content(content._id),
      (old: ContentWithProgress | undefined) => {
        if (!old) {
          return old
        }

        const withoutRawContent = (
          userInput: NonNullable<(typeof old.elements)[number]['user_input']>
        ) =>
          ({
            ...userInput,
            raw_content: undefined,
            raw_content_type: undefined,
          }) as typeof userInput

        if (elementId != null) {
          return {
            ...old,
            elements: old.elements.map((element) =>
              element.element_id === elementId && element.user_input
                ? {
                    ...element,
                    user_input: withoutRawContent(element.user_input),
                  }
                : element
            ),
          }
        }

        return {
          ...old,
          user_input: withoutRawContent(old.user_input),
        }
      }
    )
  }

  const generateBriefFromContent = (
    contentToGenerateFrom: string,
    contentType: 'html' | 'text' | 'image',
    options?: GenerateBriefFromContentOptions
  ) => {
    if (!content) {
      return
    }

    if (hasVariants && isGlobalSettings && !isModalOpenRef.current) {
      setResetWarningModalState({
        isOpen: true,
        onConfirm: () =>
          generateBriefFromContent(contentToGenerateFrom, contentType, options),
      })
      return
    }

    const selectedMergeTags =
      mergeTags?.filter((tag) => options?.mergeTagIds?.includes(tag.id)) || []

    setIsAwaitingResult(true)

    if (!isGlobalSettings && selectedElement?.element_id) {
      const wsTopic = getWsTopic({
        data: {
          contentId: content._id,
          elementId: selectedElement.element_id,
        },
        action: 'generateElementBrief',
      })

      sendWebsocketMessage({
        action: 'generateElementBrief',
        data: {
          contentId: content._id,
          elementId: selectedElement.element_id,
        },
        subscriptionAction: 'subscribe',
      })

      generateCustomBriefFromContentMutation.mutate({
        accountId,
        contentId: content._id,
        elementId: selectedElement.element_id,
        content: contentToGenerateFrom,
        contentType,
        wsTopic,
        ...(!isOptimized && { brandVoiceId: options?.brandVoiceId }),
        ...(flags.showMergeTags && { mergeTags: selectedMergeTags }),
      })
    } else {
      const wsTopic = getWsTopic({
        data: { contentId: content._id },
        action: 'generateGlobalBrief',
      })

      sendWebsocketMessage({
        action: 'generateGlobalBrief',
        data: { contentId: content._id },
        subscriptionAction: 'subscribe',
      })

      generateBriefFromContentMutation.mutate({
        accountId,
        contentId: content._id,
        content: contentToGenerateFrom,
        contentType,
        wsTopic,
        ...(!isOptimized && { brandVoiceId: options?.brandVoiceId }),
        ...(flags.showMergeTags && { mergeTags: selectedMergeTags }),
      })
    }
  }

  const isGeneratingBriefFromContent =
    generateBriefFromContentMutation.isPending ||
    generateCustomBriefFromContentMutation.isPending ||
    isAwaitingResult

  return {
    generateBriefFromContent,
    clearRawContent,
    isGeneratingBriefFromContent,
  }
}
